import {isTruthy} from "@khanacademy/wonder-stuff-core";
import {parsePerseusArticle} from "@repo/component-perseus";
import {Errors, KAError, logUnreachableCode} from "@repo/logging";

import type {StudentSubjectMasteryGoals_user_goals as GqlCourseMasteryGoal} from "#page-package/__graphql-types__/StudentSubjectMasteryGoals.ts";
import {mapCompletionStatusToChallengeStatus} from "#scratchpad-shared-package/utils/challenge-status.ts";
import type {UserAuthoredContentTypes} from "#tutorial-scratchpad-package/types.ts";
import {mapGqlVideoCaption} from "#video-package/fallback-captions/gql-type-mappers.ts";

import type {
    AssignmentData as GqlAssignment,
    AssignmentData_contents as GqlAssignmentContent,
    AssignmentData_itemCompletionStates_exerciseAttempts as GqlAssignmentExerciseAttempt,
    AssignmentData_contents_Article_topicPaths as GqlLearnableContentPath,
} from "../__graphql-types__/AssignmentData.ts";
import type {Classroom as GqlClassroom} from "../__graphql-types__/Classroom.ts";
import type {
    ContentForPath_contentRoute_listedPathData_course_courseChallenge as GqlCourseChallenge,
    ContentForPath_contentRoute_listedPathData_course as GqlListedCourse,
    ContentForPath_contentRoute_listedPathData_course_curation as GqlListedCourseCuration,
    ContentForPath_contentRoute_listedPathData_course_curation_modules as GqlListedCourseCurationModules,
    ContentForPath_contentRoute_listedPathData_course_parent as GqlListedCourseParent,
    ContentForPath_contentRoute_listedPathData_content as GqlListedLearnableContent,
    ContentForPath_contentRoute_listedPathData_course_unitChildren_allOrderedChildren_Lesson as GqlListedLesson,
    ContentForPath_contentRoute_listedPathData_course_unitChildren_allOrderedChildren_Lesson_curatedChildren as GqlListedLessonChild,
    ContentForPath_contentRoute_listedPathData_course_unitChildren as GqlListedUnit,
    ContentForPath_contentRoute_listedPathData_course_unitChildren_allOrderedChildren as GqlListedUnitChild,
    ContentForPath_contentRoute_listedPathData_course_masteryChallenge as GqlMasteryChallenge,
    ContentForPath_contentRoute_unlistedPathData_course as GqlUnlistedCourse,
    ContentForPath_contentRoute_unlistedPathData_course_curation as GqlUnlistedCourseCuration,
    ContentForPath_contentRoute_unlistedPathData_course_curation_modules as GqlUnlistedCourseCurationModules,
    ContentForPath_contentRoute_unlistedPathData_course_parent as GqlUnlistedCourseParent,
    ContentForPath_contentRoute_unlistedPathData_content as GqlUnlistedLearnableContent,
    ContentForPath_contentRoute_unlistedPathData_course_unitChildren_allOrderedChildren_Lesson as GqlUnlistedLesson,
    ContentForPath_contentRoute_unlistedPathData_course_unitChildren_allOrderedChildren_Lesson_curatedChildren as GqlUnlistedLessonChild,
    ContentForPath_contentRoute_unlistedPathData_course_unitChildren as GqlUnlistedUnit,
    ContentForPath_contentRoute_unlistedPathData_course_unitChildren_allOrderedChildren as GqlUnlistedUnitChild,
} from "../__graphql-types__/ContentForPath.ts";
import type {
    LearnableContentData_Article as GqlArticle,
    LearnableContentData_Video_videoAuthorList as GqlAuthor,
    LearnableContentData_Challenge as GqlChallenge,
    LearnableContentData_Exercise as GqlExercise,
    LearnableContentData_Interactive as GqlInteractive,
    LearnableContentData_Video_keyMoments as GqlKeyMoment,
    LearnableContentData_Project as GqlProject,
    LearnableContentData_TopicQuiz as GqlQuiz,
    LearnableContentData_TopicQuiz_coveredTutorials as GqlQuizCoveredTutorials,
    LearnableContentData_Talkthrough as GqlTalkthrough,
    LearnableContentData_TopicUnitTest as GqlUnitTest,
    LearnableContentData_Video as GqlVideo,
    LearnableContentData_Video_thumbnailUrls as GqlVideoThumbnailUrls,
} from "../__graphql-types__/LearnableContentData.ts";
import type {MappedStandardsForContent_standards as GqlPublishedStandard} from "../__graphql-types__/MappedStandardsForContent.ts";
import type {QuizMetadata as GqlQuizMetadata} from "../__graphql-types__/QuizMetadata.ts";
import type {UnitTestMetadata as GqlUnitTestMetadata} from "../__graphql-types__/UnitTestMetadata.ts";
import type {UserAssignmentsPage_user_assignmentsPage as GqlAssignmentsPage} from "../__graphql-types__/UserAssignmentsPage.ts";
import type {
    userProgressForLesson_user_contentItemProgresses_VideoItemProgress_bestScore as GqlBestScore,
    userProgressForLesson_user_contentItemProgresses as GqlContentItemProgress,
    userProgressForLesson_user_contentItemProgresses_ExerciseItemProgress as GqlExerciseItemProgress,
    userProgressForLesson_user_contentItemProgresses_ExerciseItemProgress_lastCompletedAttempt as GqlLastCompletedAttempt,
    userProgressForLesson_user_latestQuizAttempts as GqlLatestQuizAttempt,
    userProgressForLesson_user_latestUnitTestAttempts as GqlLatestUnitTestAttempt,
} from "../__graphql-types__/userProgressForLesson.ts";
import type {ProgramProgress} from "../data-access/progress/use-program-progress.ts";
import {toContentKind} from "../ui/utils/content-util.ts";

import type {
    Article,
    Assignment,
    AssignmentCompletionState,
    AssignmentContent,
    AssignmentContentUnit,
    AssignmentsPage,
    Author,
    BasicContentItemProgress,
    BestScore,
    Challenge,
    Classroom,
    ComputingCodeFormat,
    ComputingTestsFormat,
    ContentItemProgress,
    Course,
    CourseChallenge,
    CourseCuration,
    CourseCurationModule,
    CourseMasteryGoal,
    CourseParent,
    CoveredLesson,
    CurationNodeKind,
    Exercise,
    ExerciseItemProgress,
    Interactive,
    KeyMoment,
    LastCompletedAttempt,
    LatestQuizOrUnitTestAttempt,
    LearnableContent,
    LearnableContentKind,
    Lesson,
    LessonChild,
    MasteryChallenge,
    Project,
    PublishedStandard,
    Quiz,
    QuizMetadata,
    Talkthrough,
    TopicPath,
    TranslatedPerseusContent,
    Unit,
    UnitChild,
    UnitTest,
    UnitTestMetadata,
    UserProgram,
    Video,
} from "./types.ts";

/**
 * mapGqlCourse converts a GqlCourse type to a Course type used for content
 * rendering.
 */
export const mapGqlCourse = (
    course: GqlListedCourse | GqlUnlistedCourse,
): Course => {
    return {
        id: course.id,
        contentKind: "Course",
        iconPath: course.iconPath,
        masteryEnabled: _invariant<boolean>(
            course.masteryEnabled,
            "masteryEnabled",
        ),
        parent: _mapGqlCourseParent(
            _invariant<GqlListedCourseParent | GqlUnlistedCourseParent>(
                course.parent,
                "parent",
            ),
        ),
        relativeUrl: _invariant<string>(course.relativeUrl, "relativeUrl"),
        slug: _invariant<string>(course.slug, "slug"),
        translatedTitle: _invariant<string>(
            course.translatedTitle,
            "translatedTitle",
        ),
        translatedDescription: _invariant<string>(
            course.translatedDescription,
            "translatedDescription",
        ),
        unitChildren: (course.unitChildren ?? [])
            .filter(isTruthy)
            .map(mapGqlUnit),
        curation: _mapGqlCourseCuration(
            _invariant(course.curation, "curation"),
        ),
        lowerToc: course.lowerToc ?? true,
        courseChallenge: course.courseChallenge
            ? mapGqlCourseChallenge(course.courseChallenge)
            : null,
        isListedForLearners: _invariant(
            course.isListedForLearners,
            "isListedForLearners",
        ),
        translatedCustomTitleTag: course.translatedCustomTitleTag,
        masteryChallenge: course.masteryChallenge
            ? mapGqlMasteryChallenge(course.masteryChallenge)
            : null,
        userAuthoredContentTypes: _invariant(
            course.userAuthoredContentTypes?.map((type, index) =>
                _invariant(type, `userAuthoredContentTypes[${index}]`),
            ),
            "userAuthoredContentTypes",
        ),
        masterableExerciseIdsWithDuplicates: (
            course.masterableExercises ?? []
        ).map((exercise, index) =>
            _invariant(exercise?.id, `masterableExercises[${index}].id`),
        ),
    };
};

export const mapGqlCourseChallenge = (
    courseChallenge: GqlCourseChallenge,
): CourseChallenge => {
    if (courseChallenge.contentKind !== "CourseChallenge") {
        throw new KAError("Failed to map course challenge", Errors.Internal, {
            sentryData: {
                contexts: {
                    Data: {
                        id: courseChallenge.id,
                    },
                },
            },
        });
    }

    return {
        id: courseChallenge.id,
        slug: _invariant(courseChallenge.slug, "slug"),
        contentDescriptor: _invariant(
            courseChallenge.contentDescriptor,
            "contentDescriptor",
        ),
        contentKind: "CourseChallenge",
        urlWithinCurationNode: _invariant(
            courseChallenge.urlWithinCurationNode,
            "urlWithinCurationNode",
        ),
        exerciseLength: _invariant(
            courseChallenge.exerciseLength,
            "exerciseLength",
        ),
        timeEstimate: {
            lowerBound: _invariant(
                courseChallenge.timeEstimate?.lowerBound,
                "timeEstimate.lowerBound",
            ),
            upperBound: _invariant(
                courseChallenge.timeEstimate?.upperBound,
                "timeEstimate.upperBound",
            ),
        },
    };
};

const _mapGqlCourseCuration = (
    curation: GqlListedCourseCuration | GqlUnlistedCourseCuration,
): CourseCuration => {
    return {
        hideCourseIntro: _invariant<boolean>(
            curation.hideSubjectIntro,
            "hideSubjectIntro",
        ),
        hideCommunityQuestions: curation.hideCommunityQuestions,
        sponsorFooterAttribution: curation.sponsorFooterAttribution
            ? {
                  footnoteHtml:
                      curation.sponsorFooterAttribution.footnoteHtml ??
                      undefined,
                  imageBaselineAligned:
                      curation.sponsorFooterAttribution.imageBaselineAligned ??
                      undefined,
                  imageCaption:
                      curation.sponsorFooterAttribution.imageCaption ??
                      undefined,
                  imageUrl:
                      curation.sponsorFooterAttribution.imageUrl ?? undefined,
                  taglineHtml:
                      curation.sponsorFooterAttribution.taglineHtml ??
                      undefined,
              }
            : null,
        modules: (curation.modules ?? [])
            .map(_mapGqlCourseCurationModule)
            .filter(isTruthy),
        excludedChildren: _invariant(
            curation.excludedChildren,
            "excludedChildren",
        ),
    };
};

export const _mapGqlCourseCurationModule = (
    module: GqlListedCourseCurationModules | GqlUnlistedCourseCurationModules,
): CourseCurationModule | null | undefined => {
    switch (module.__typename) {
        case "ActionListModule": {
            return {
                kind: "ActionList",
                actions: module.actions.map((action) => ({
                    contentDescriptor: action.contentDescriptor,
                    text: action.text,
                    url: action.URL,
                })),
                title: module.title,
                untranslatedFields: module.untranslatedFields,
            };
        }
        case "BrowseProjectsModule":
            return {
                kind: "BrowseProjects",
            };
        case "CourseIntroModule":
            return {
                kind: "SubjectIntro",
                callToAction: module.callToAction,
                description: module.description,
                link: module.link,
                video: module.video || null,
                untranslatedFields: module.untranslatedFields,
                title: module.title,
            };
        case "MappersModule": {
            return {
                kind: "Mappers",
            };
        }
        case "PartnershipDescriptionModule": {
            return {
                kind: "PartnershipDescription",
                description: module.description,
                imageCaption: module.imageCaption,
                imageUrl: module.imageUrl,
                isOutro: module.isOutro,
                partnerUrl: module.partnerUrl,
                partnerUrlText: module.partnerUrlText,
                untranslatedFields: module.untranslatedFields,
            };
        }
        case "ContentCarouselModule": {
            return {
                kind: "ContentCarousel",
                referrer: module.referrer,
                title: module.title,
                contentDescriptors: module.contentDescriptors,
                untranslatedFields: module.untranslatedFields,
            };
        }
    }
};

export const mapGqlMasteryChallenge = (
    masteryChallenge: GqlMasteryChallenge,
): MasteryChallenge => {
    if (masteryChallenge.contentKind !== "CourseMasteryChallenge") {
        throw new KAError("Failed to map mastery challenge", Errors.Internal, {
            sentryData: {
                contexts: {
                    Data: {
                        id: masteryChallenge.id,
                    },
                },
            },
        });
    }

    return {
        id: masteryChallenge.id,
        slug: _invariant(masteryChallenge.slug, "slug"),
        contentDescriptor: _invariant(
            masteryChallenge.contentDescriptor,
            "contentDescriptor",
        ),
        contentKind: "MasteryChallenge",
        urlWithinCurationNode: _invariant(
            masteryChallenge.urlWithinCurationNode,
            "urlWithinCurationNode",
        ),
        exerciseLength: _invariant(
            masteryChallenge.exerciseLength,
            "exerciseLength",
        ),
        timeEstimate: {
            lowerBound: _invariant(
                masteryChallenge.timeEstimate?.lowerBound,
                "timeEstimate.lowerBound",
            ),
            upperBound: _invariant(
                masteryChallenge.timeEstimate?.upperBound,
                "timeEstimate.upperBound",
            ),
        },
    };
};

const _mapGqlCourseParent = (
    parent: GqlListedCourseParent | GqlUnlistedCourseParent,
): CourseParent => {
    return {
        id: parent.id,
        contentKind: _invariant<CurationNodeKind>(
            parent.contentKind as CurationNodeKind | null | undefined,
            "contentKind",
        ),
        relativeUrl: _invariant<string>(parent.relativeUrl, "relativeUrl"),
        slug: _invariant<string>(parent.slug, "slug"),
        translatedTitle: _invariant<string>(
            parent.translatedTitle,
            "translatedTitle",
        ),
    };
};

/**
 * mapGqlUnit converts a GqlqlUnit type to a Unit type used for content
 * rendering.
 */
export const mapGqlUnit = (unit: GqlListedUnit | GqlUnlistedUnit): Unit => {
    return {
        id: unit.id,
        contentKind: "Unit",
        children: (unit.allOrderedChildren ?? [])
            .filter(isTruthy)
            .map(_mapGqlUnitChild),
        masteryEnabled: _invariant<boolean>(
            unit.masteryEnabled,
            "masteryEnabled",
        ),
        slug: _invariant<string>(unit.slug, "slug"),
        relativeUrl: _invariant<string>(unit.relativeUrl, "relativeUrl"),
        translatedDescription: _invariant<string>(
            unit.translatedDescription,
            "translatedDescription",
        ),
        translatedTitle: _invariant<string>(
            unit.translatedTitle,
            "translatedTitle",
        ),
        iconPath: unit.iconPath,
        translatedCustomTitleTag: unit.translatedCustomTitleTag,
        isListedForLearners: _invariant(
            unit.isListedForLearners,
            "isListedForLearners",
        ),
    };
};

const _mapGqlUnitChild = (
    child: GqlListedUnitChild | GqlUnlistedUnitChild,
): UnitChild => {
    switch (child.__typename) {
        case "Lesson":
            return mapGqlLesson(child);
        case "TopicQuiz":
        case "TopicUnitTest":
            const mappedChild = mapGqlLearnableContentMetadata(child);
            // In practice, this should never happen and this check is primarily
            // here to satisfy TypeScript since it doesn't know that we can only
            // get Quiz/UnitTestMetadata as a result.
            if (
                mappedChild.contentKind !== "Quiz" &&
                mappedChild.contentKind !== "UnitTest"
            ) {
                throw new KAError(
                    "Failed to map Quiz/UnitTest",
                    Errors.Internal,
                    {
                        sentryData: {
                            contexts: {
                                Data: {
                                    id: child.id,
                                },
                            },
                        },
                    },
                );
            }
            return mappedChild;

        default:
            throw new KAError(
                // @ts-expect-error - TS2339 - Property '__typename' does not exist on type 'never'.
                "Invalid Unit child type: " + child.__typename,
                Errors.Internal,
                {
                    sentryData: {
                        contexts: {
                            Data: {
                                // @ts-expect-error - TS2339 - Property 'id' does not exist on type 'never'.
                                id: child.id,
                            },
                        },
                    },
                },
            );
    }
};

/**
 * mapGqlLesson converts a GqlLesson type to a Lesson type used for content
 * rendering.
 */
export const mapGqlLesson = (
    lesson: GqlListedLesson | GqlUnlistedLesson,
): Lesson => {
    const children: Array<LessonChild> = (lesson.curatedChildren ?? []).reduce<
        Array<any>
    >((children, child) => {
        if (!child) {
            return children;
        }

        const mappedChild = mapGqlLearnableContentMetadata(child);
        if (
            mappedChild.contentKind === "Quiz" ||
            mappedChild.contentKind === "UnitTest"
        ) {
            return children;
        }

        children.push(mappedChild);
        return children;
    }, []);

    return {
        id: lesson.id,
        contentKind: "Lesson",
        curatedChildren: children,
        relativeUrl: _invariant<string>(lesson.relativeUrl, "relativeUrl"),
        slug: _invariant<string>(lesson.slug, "slug"),
        translatedDescription: lesson.translatedDescription ?? "",
        translatedTitle: _invariant<string>(
            lesson.translatedTitle,
            "translatedTitle",
        ),
        key: _invariant<string>(lesson.key, "key"),
    };
};

export const mapGqlLearnableContentPath = (
    topicPath: GqlLearnableContentPath,
): TopicPath => {
    const path = topicPath.path ?? [];
    if (path.length === 0) {
        throw new KAError(
            "LearnableContent paths must not be empty",
            Errors.Internal,
        );
    }

    const learnableContent = path[path.length - 1];

    if (learnableContent?.kind === "CourseChallenge") {
        if (path.length !== 3) {
            throw new KAError(
                "CourseChallenge paths are expected to contain 3 segments",
                Errors.Internal,
                {
                    sentryData: {
                        contexts: {
                            Data: {
                                pathLength: path.length,
                            },
                        },
                    },
                },
            );
        }

        const [domain, course, _] = path;

        return {
            pathKind: "course-level-learnable-content",
            domainId: _invariant<string>(domain?.id, "topicPath.path.id"),
            domainSlug: _invariant<string>(domain?.slug, "topicPath.path.slug"),
            courseId: _invariant<string>(course?.id, "topicPath.path.id"),
            courseSlug: _invariant<string>(course?.slug, "topicPath.path.slug"),
            learnableContentKind: learnableContent?.kind,
            learnableContentId: _invariant<string>(
                learnableContent?.id,
                "topicPath.path.id",
            ),
            learnableContentSlug: _invariant<string>(
                learnableContent?.slug,
                "topicPath.path.slug",
            ),
        };
    }
    if (path.length !== 5) {
        throw new KAError(
            "LearnableContent paths are expected to contain 5 segments",
            Errors.Internal,
            {
                sentryData: {
                    contexts: {
                        Data: {
                            pathLength: path.length,
                        },
                    },
                },
            },
        );
    }

    const [domain, course, unit, lesson, _] = path;

    return {
        pathKind: "lesson-level-learnable-content",
        domainId: _invariant<string>(domain?.id, "topicPath.path.id"),
        domainSlug: _invariant<string>(domain?.slug, "topicPath.path.slug"),
        courseId: _invariant<string>(course?.id, "topicPath.path.id"),
        courseSlug: _invariant<string>(course?.slug, "topicPath.path.slug"),
        unitId: _invariant<string>(unit?.id, "topicPath.path.id"),
        unitSlug: _invariant<string>(unit?.slug, "topicPath.path.slug"),
        lessonId: _invariant<string>(lesson?.id, "topicPath.path.id"),
        lessonSlug: _invariant<string>(lesson?.slug, "topicPath.path.slug"),
        learnableContentKind: toContentKind(
            _invariant<string>(learnableContent?.kind, "topicPath.path.kind"),
        ),
        learnableContentId: _invariant<string>(
            learnableContent?.id,
            "topicPath.path.id",
        ),
        learnableContentSlug: _invariant<string>(
            learnableContent?.slug,
            "topicPath.path.slug",
        ),
    };
};

/**
 * Exported for testing. Maps a Gql LearnableContent child type to a
 * LessonChild.
 */
export const mapGqlLearnableContentMetadata = (
    child:
        | GqlListedLessonChild
        | GqlUnlistedLessonChild
        | GqlQuizMetadata
        | GqlUnitTestMetadata,
): LessonChild | QuizMetadata | UnitTestMetadata => {
    const contentKind = _invariant<LearnableContentKind>(
        child.contentKind as LearnableContentKind | null | undefined,
        "contentKind",
    );

    const mappedChild = {
        id: child.id,
        canonicalUrl: _invariant(child.canonicalUrl, "canonicalUrl"),
        contentDescriptor: _invariant(
            child.contentDescriptor,
            "contentDescriptor",
        ),
        progressKey: child.progressKey,
        slug: _invariant<string>(child.slug, "slug"),
        translatedCustomTitleTag: child.translatedCustomTitleTag,
        translatedDescription: _invariant<string>(
            child.translatedDescription,
            "translatedDescription",
        ),
        translatedTitle: _invariant<string>(
            child.translatedTitle,
            "translatedTitle",
        ),
        urlWithinCurationNode: _invariant<string>(
            child.urlWithinCurationNode,
            "urlWithinCurationNode",
        ),
        unitMasteryEnabled: Boolean(
            child.parentTopic?.parent?.masteryEnabled ?? false,
        ),
    } as const;

    switch (child.__typename) {
        case "AIGuideActivity":
            throw new KAError(
                "AIGuideActivity is not supported",
                Errors.Internal,
            );
        case "TopicQuiz":
            return {
                ...mappedChild,
                contentKind: "Quiz",
                exerciseLength: _invariant<number>(
                    child.exerciseLength,
                    "exerciseLength",
                ),
                index: _invariant<number>(child.index, "index"),
                timeEstimate: {
                    lowerBound: _invariant<number>(
                        child.timeEstimate?.lowerBound,
                        "timeEstimate.lowerBound",
                    ),
                    upperBound: _invariant<number>(
                        child.timeEstimate?.upperBound,
                        "timeEstimate.upperBound",
                    ),
                },
            } as QuizMetadata;

        case "TopicUnitTest":
            return {
                ...mappedChild,
                contentKind: "UnitTest",
                exerciseLength: _invariant<number>(
                    child.exerciseLength,
                    "exerciseLength",
                ),
                timeEstimate: {
                    lowerBound: _invariant<number>(
                        child.timeEstimate?.lowerBound,
                        "timeEstimate.lowerBound",
                    ),
                    upperBound: _invariant<number>(
                        child.timeEstimate?.upperBound,
                        "timeEstimate.upperBound",
                    ),
                },
            } as UnitTestMetadata;

        case "Exercise":
            return {
                ...mappedChild,
                contentKind: "Exercise",
                exerciseLength: _invariant<number>(
                    child.exerciseLength,
                    "exerciseLength",
                ),
                isSkillCheck: _invariant<boolean>(
                    child.isSkillCheck,
                    "isSkillCheck",
                ),
                sponsored: _invariant<boolean>(child.sponsored, "sponsored"),
                thumbnailUrl: _invariant<string>(
                    child.thumbnailUrl,
                    "thumbnailUrl",
                ),
                timeEstimate: {
                    lowerBound: _invariant<number>(
                        child.timeEstimate?.lowerBound,
                        "timeEstimate.lowerBound",
                    ),
                    upperBound: _invariant<number>(
                        child.timeEstimate?.upperBound,
                        "timeEstimate.upperBound",
                    ),
                },
            };

        case "Challenge":
            return {
                ...mappedChild,
                contentKind: "Challenge",
                userAuthoredContentType: _mapToUserAuthoredContentType(
                    child.userAuthoredContentType,
                ),
            };
        case "Interactive":
            return {
                ...mappedChild,
                contentKind: "Interactive",
                userAuthoredContentType: _mapToUserAuthoredContentType(
                    child.userAuthoredContentType,
                ),
            };
        case "Project":
            return {
                ...mappedChild,
                contentKind: "Project",
                userAuthoredContentType: _mapToUserAuthoredContentType(
                    child.userAuthoredContentType,
                ),
            };
        case "Article":
        case "Talkthrough":
        case "Video":
            return {...mappedChild, contentKind} as LessonChild;
        default:
            // This should naver happen so long as our switch case is
            // exhaustive, but it's here for good measure :).
            throw logUnreachableCode("Unhandled content kind", child);
    }
};

/**
 * mapGqlArticle converts a GqlArticle type to an Article type used for
 * content rendering.
 */
export const mapGqlArticle = (content: GqlArticle): Article => {
    return {
        clarificationsEnabled: content.articleClarificationsEnabled || false,
        contentKind: "Article",
        id: content.id,
        translatedPerseusContent: _mapPerseusContent(
            content.translatedPerseusContent,
        ),
        slug: _invariant<string>(content.slug, "slug"),
        standards: _mapGqlPublishedStandards([]),
        translatedDescription: _invariant<string>(
            content.translatedDescription,
            "translatedDescription",
        ),
        translatedTitle: _invariant<string>(
            content.translatedTitle,
            "translatedTitle",
        ),
    };
};

function _mapPerseusContent(
    translatedPerseusContent?: string | null,
): TranslatedPerseusContent {
    if (!translatedPerseusContent) {
        return [];
    }
    return parsePerseusArticle(translatedPerseusContent, "9183202");
}

/**
 * mapGqlChallenge converts a GqlChallenge type to a Challenge type used for
 * content rendering.
 */
export const mapGqlChallenge = (content: GqlChallenge): Challenge => {
    return {
        id: content.id,
        authorList: content.authorList.map(_mapGqlAuthor).filter(isTruthy),
        canvasOnly: content.canvasOnly ?? false,
        code: _invariant<string>(content.code, "code"),
        codeFormat: _invariant<ComputingCodeFormat>(
            content.codeFormat,
            "codeFormat",
        ),
        configVersion: content.configVersion ?? 0,
        contentKind: "Challenge",
        defaultUrlPath: _invariant<string>(
            content.defaultUrlPath,
            "defaultUrlPath",
        ),
        height: _invariant<number>(content.height, "height"),
        nodeSlug: _invariant<string>(content.nodeSlug, "nodeSlug"),
        slug: _invariant<string>(content.slug, "slug"),
        translatedDescription: _invariant<string>(
            content.translatedDescription,
            "translatedDescription",
        ),
        translatedTests: _invariant<string>(
            content.translatedTests,
            "translatedTests",
        ),
        testsFormat: _invariant<ComputingTestsFormat>(
            content.testsFormat,
            "testsFormat",
        ),
        translatedTitle: _invariant<string>(
            content.translatedTitle,
            "translatedTitle",
        ),
        testStrings: content.testStrings.map(
            (testString) => testString.message,
        ),
        userAuthoredContentType: _mapToUserAuthoredContentType(
            content.userAuthoredContentType,
        ),
        width: _invariant<number>(content.width, "width"),
    };
};

/**
 * mapGqlExercise converts a GqlExercise type to an Exercise type used for
 * content rendering.
 */
export const mapGqlExercise = (content: GqlExercise): Exercise => {
    return {
        id: content.id,
        contentKind: "Exercise",
        slug: _invariant<string>(content.slug, "slug"),
        standards: _mapGqlPublishedStandards([]),
        translatedTitle: _invariant<string>(
            content.translatedTitle,
            "translatedTitle",
        ),
        problemTypeKind: content.problemTypeKind,
    };
};

/**
 * mapGqlInteractive converts a GqlInteractive type to an Interactive type
 * used for content rendering.
 */
export const mapGqlInteractive = (content: GqlInteractive): Interactive => {
    return {
        id: content.id,
        authorList: content.authorList.map(_mapGqlAuthor).filter(isTruthy),
        canvasOnly: content.canvasOnly ?? false,
        code: _invariant<string>(content.code, "code"),
        codeFormat: _invariant<ComputingCodeFormat>(
            content.codeFormat,
            "codeFormat",
        ),
        configVersion: content.configVersion ?? 0,
        contentKind: "Interactive",
        defaultUrlPath: _invariant<string>(
            content.defaultUrlPath,
            "defaultUrlPath",
        ),
        height: _invariant<number>(content.height, "height"),
        nodeSlug: _invariant<string>(content.nodeSlug, "nodeSlug"),
        slug: _invariant<string>(content.slug, "slug"),
        translatedDescription: _invariant<string>(
            content.translatedDescription,
            "translatedDescription",
        ),
        translatedTitle: _invariant<string>(
            content.translatedTitle,
            "translatedTitle",
        ),
        userAuthoredContentType: _mapToUserAuthoredContentType(
            content.userAuthoredContentType,
        ),
        width: _invariant<number>(content.width, "width"),
    };
};

/**
 * mapGqlProject converts a GqlProject type to a Project type used for
 * content rendering.
 */
export const mapGqlProject = (content: GqlProject): Project => {
    return {
        id: content.id,
        authorList: content.authorList.map(_mapGqlAuthor).filter(isTruthy),
        canvasOnly: content.canvasOnly ?? false,
        code: _invariant<string>(content.code, "code"),
        codeFormat: _invariant<ComputingCodeFormat>(
            content.codeFormat,
            "codeFormat",
        ),
        configVersion: content.configVersion ?? 0,
        contentKind: "Project",
        defaultUrlPath: _invariant<string>(
            content.defaultUrlPath,
            "defaultUrlPath",
        ),
        height: _invariant<number>(content.height, "height"),
        nodeSlug: _invariant<string>(content.nodeSlug, "nodeSlug"),
        slug: _invariant<string>(content.slug, "slug"),
        translatedDescription: _invariant<string>(
            content.translatedDescription,
            "translatedDescription",
        ),
        translatedTitle: _invariant<string>(
            content.translatedTitle,
            "translatedTitle",
        ),
        translatedProjectEval: _invariant<string>(
            content.translatedProjectEval,
            "translatedProjectEval",
        ),
        translatedProjectEvalTips: content.translatedProjectEvalTips,
        userAuthoredContentType: _mapToUserAuthoredContentType(
            content.userAuthoredContentType,
        ),
        width: _invariant<number>(content.width, "width"),
    };
};

const _mapGqlCoveredLessons = (
    coveredTutorials?: Array<GqlQuizCoveredTutorials | null | undefined> | null,
): Array<CoveredLesson> => {
    if (!coveredTutorials) {
        return [];
    }

    return coveredTutorials.filter(isTruthy).map((lesson) => ({
        id: lesson.id,
        relativeUrl: _invariant(lesson.relativeUrl, "relativeUrl"),
        translatedTitle: _invariant(lesson.translatedTitle, "translatedTitle"),
        learnableContent:
            lesson.allLearnableContent?.filter(isTruthy).map((lc) => ({
                id: lc.id,
                contentKind: lc.contentKind as LearnableContentKind,
            })) ?? [],
    }));
};

/**
 * mapGqlQuiz converts a GqlQuiz type to a Quiz type used for content
 * rendering.
 */
export const mapGqlQuiz = (content: GqlQuiz): Quiz => {
    return {
        contentKind: "Quiz",
        coveredLessons: _mapGqlCoveredLessons(content.coveredTutorials),
        exerciseLength: _invariant<number>(
            content.exerciseLength,
            "exerciseLength",
        ),
        id: content.id,
        index: _invariant<number>(content.index, "index"),
        slug: _invariant<string>(content.slug, "slug"),
        timeEstimate: {
            lowerBound: _invariant<number>(
                content.timeEstimate.lowerBound,
                "timeEstimate.lowerBound",
            ),
            upperBound: _invariant<number>(
                content.timeEstimate.upperBound,
                "timeEstimate.upperBound",
            ),
        },
        translatedTitle: _invariant<string>(
            content.translatedTitle,
            "translatedTitle",
        ),
    };
};

/**
 * mapGqlTalkthrough converts a GqlTalkthrough type to a Talkthrough type used
 * for content rendering.
 */
export const mapGqlTalkthrough = (content: GqlTalkthrough): Talkthrough => {
    return {
        id: content.id,
        authorList: content.authorList.map(_mapGqlAuthor).filter(isTruthy),
        canvasOnly: content.canvasOnly ?? false,
        code: _invariant<string>(content.code, "code"),
        configVersion: content.configVersion ?? 0,
        contentKind: "Talkthrough",
        defaultUrlPath: _invariant<string>(
            content.defaultUrlPath,
            "defaultUrlPath",
        ),
        height: _invariant<number>(content.height, "height"),
        nodeSlug: _invariant<string>(content.nodeSlug, "nodeSlug"),
        playback: _invariant<string>(content.playback, "playback"),
        slug: _invariant<string>(content.slug, "slug"),
        // Not every Talkthrough has subtitles so we don't wrap the field
        // with the _invariant assertion.
        subtitles: mapGqlVideoCaption(
            content.subtitles?.filter(isTruthy) ?? [],
        ),
        translatedDescription: _invariant<string>(
            content.translatedDescription,
            "translatedDescription",
        ),
        translatedMp3Url: _invariant<string>(
            content.translatedMp3Url,
            "translatedMp3Url",
        ),
        translatedTitle: _invariant<string>(
            content.translatedTitle,
            "translatedTitle",
        ),
        userAuthoredContentType: _invariant<UserAuthoredContentTypes>(
            content.userAuthoredContentType as
                | UserAuthoredContentTypes
                | null
                | undefined,
            "userAuthoredContentType",
        ),
        width: _invariant<number>(content.width, "width"),
        youtubeId: _invariant<string>(content.youtubeId, "youtubeId"),
    };
};

/**
 * mapGqlUnitTest converts a GqlUnitTest type to a UnitTest type used for
 * content rendering.
 */
export const mapGqlUnitTest = (content: GqlUnitTest): UnitTest => {
    return {
        contentKind: "UnitTest",
        coveredLessons: _mapGqlCoveredLessons(content.coveredTutorials),
        exerciseLength: _invariant<number>(
            content.exerciseLength,
            "exerciseLength",
        ),
        id: content.id,
        slug: _invariant<string>(content.slug, "slug"),
        timeEstimate: {
            lowerBound: _invariant<number>(
                content.timeEstimate.lowerBound,
                "timeEstimate.lowerBound",
            ),
            upperBound: _invariant<number>(
                content.timeEstimate.upperBound,
                "timeEstimate.upperBound",
            ),
        },
        translatedTitle: _invariant<string>(
            content.translatedTitle,
            "translatedTitle",
        ),
    };
};

const _mapGqlDownloadUrls = (
    gqlDownloadUrls?: string | null,
): Record<string, string> => {
    if (!gqlDownloadUrls) {
        return {};
    }

    return JSON.parse(gqlDownloadUrls);
};

const _mapGqlAuthor = (
    author?: GqlAuthor | null,
): Author | null | undefined => {
    if (!author?.name) {
        return null;
    }

    return {name: author.name};
};

const _mapGqlThumbnailUrls = (
    gqlThumbnailUrls?: Array<GqlVideoThumbnailUrls | null | undefined> | null,
): Array<{
    category: string;
    url: string;
}> => {
    if (!gqlThumbnailUrls) {
        return [];
    }

    return gqlThumbnailUrls
        .map((item) => {
            if (!item || !item.category || !item.url) {
                return null;
            }

            return {
                category: item.category,
                url: item.url,
            };
        })
        .filter(isTruthy);
};

const _mapGqlPublishedStandards = (
    gqlPublishedStandards?: Array<
        GqlPublishedStandard | null | undefined
    > | null,
): Array<PublishedStandard> => {
    if (!gqlPublishedStandards) {
        return [];
    }

    return gqlPublishedStandards.filter(isTruthy).map((item) => ({
        setId: _invariant(item.setId, "setId"),
        standardId: _invariant(item.standardId, "standardId"),
        // TODO (LP-12279) Figure out if this should actually be nullable,
        // or if this needs to be fixed on the content side.
        description: item.description ?? "",
        relativeUrl: item.relativeUrl,
        setOfStandards: {
            id: _invariant(item.setOfStandards.id, "id"),
            isDiscoverable: _invariant(
                item.setOfStandards.isDiscoverable,
                "isDiscoverable",
            ),
        },
    }));
};

/**
 * mapGqlVideo converts a GqlVideo type to a Video type used for content
 * rendering.
 */
export const mapGqlVideo = (content: GqlVideo): Video => {
    return {
        id: content.id,
        slug: _invariant<string>(content.slug, "slug"),
        contentKind: "Video",
        translatedTitle: _invariant<string>(
            content.translatedTitle,
            "translatedTitle",
        ),
        authorNames: (content.authorNames ?? []).filter(isTruthy),
        authorList: (content.videoAuthorList || [])
            .map(_mapGqlAuthor)
            .filter(isTruthy),
        clarificationsEnabled: content.clarificationsEnabled,
        dateAdded: _invariant<string>(content.dateAdded, "dateAdded"),
        description: _invariant<string>(content.description, "description"),
        downloadUrls: _mapGqlDownloadUrls(content.downloadUrls),
        duration: _invariant<number>(content.duration, "duration"),
        imageUrl: _invariant<string>(content.imageUrl, "imageUrl"),
        kaUrl: _invariant<string>(content.kaUrl, "kaUrl"),
        kaUserLicense: _invariant<string>(
            content.kaUserLicense,
            "kaUserLicense",
        ),
        keywords: _invariant<string>(content.keywords, "keywords"),
        readableId: _invariant<string>(content.readableId, "readableId"),
        sha: _invariant<string>(content.sha, "sha"),
        thumbnailUrls: _mapGqlThumbnailUrls(content.thumbnailUrls),
        translatedDescriptionHtml: _invariant<string>(
            content.translatedDescriptionHtml,
            "translatedDescriptionHtml",
        ),
        translatedYoutubeId: _invariant<string>(
            content.translatedYoutubeId,
            "translatedYoutubeId",
        ),
        translatedYoutubeLang: _invariant<string>(
            content.translatedYoutubeLang,
            "translatedYoutubeLang",
        ),
        youtubeId: _invariant<string>(content.youtubeId, "youtubeId"),
        augmentedTranscript: content.augmentedTranscript,
        descriptionHtml: _invariant<string>(
            content.descriptionHtml,
            "descriptionHtml",
        ),
        nodeSlug: _invariant<string>(content.nodeSlug, "nodeSlug"),
        relativeUrl: _invariant<string>(content.relativeUrl, "relativeUrl"),
        translatedCustomTitleTag: _invariant<string>(
            content.translatedCustomTitleTag,
            "translatedCustomTitleTag",
        ),
        translatedDescription: _invariant<string>(
            content.translatedDescription,
            "translatedDescription",
        ),
        standards: _mapGqlPublishedStandards([]),
        subtitles: mapGqlVideoCaption(
            _invariant(content.subtitles?.filter(isTruthy), "subtitles"),
        ),
        keyMoments: _mapGqlKeyMoments(content.keyMoments),
        educationalLevel: content.educationalLevel,
        learningResourceType: content.learningResourceType,
    };
};

export const _mapGqlKeyMoments = (
    keyMoments?: Array<GqlKeyMoment> | null,
): ReadonlyArray<KeyMoment> | null | undefined => {
    return keyMoments?.map((keyMoment) => {
        const {__typename: _, ...rest} = keyMoment;
        return {...rest};
    });
};

/**
 * mapGqlLearnableContent converts a Gql(Listed|Unlisted)LearnableContent type
 * to a LearnableContent type used for content rendering.
 */
export const mapGqlLearnableContent = (
    content: GqlListedLearnableContent | GqlUnlistedLearnableContent,
): LearnableContent => {
    switch (content.__typename) {
        case "Article":
            return mapGqlArticle(content);

        case "Challenge":
            return mapGqlChallenge(content);

        case "Exercise":
            return mapGqlExercise(content);

        case "Interactive":
            return mapGqlInteractive(content);

        case "Project":
            return mapGqlProject(content);

        case "Talkthrough":
            return mapGqlTalkthrough(content);

        case "TopicQuiz":
            return mapGqlQuiz(content);

        case "TopicUnitTest":
            return mapGqlUnitTest(content);

        case "Video":
            return mapGqlVideo(content);

        case "LearnableCourseChallenge":
            throw new KAError(
                "CourseChallenge learnable content not supported",
                Errors.Internal,
            );

        case "AIGuideActivity":
            throw new KAError(
                "AIGuideActivity learnable content not supported",
                Errors.Internal,
            );

        default:
            // This should naver happen so long as our switch case is
            // exhaustive, but it's here for good measure :).
            // @ts-expect-error - TS2345 - Argument of type 'any' is not assignable to parameter of type 'never'. | TS2339 - Property '__typename' does not exist on type 'never'.
            throw logUnreachableCode("Unhandled typename", content.__typename);
    }
};

/**
 * mapGqlClassroom converts a GQL type for a StudentList to the expected
 * classroom type used in useClassroom.
 */
export const mapGqlClassroom = (
    classroom?: GqlClassroom | null,
): Classroom | null | undefined => {
    if (!classroom) {
        return null;
    }
    return {
        coachKaid: classroom.coachKaid,
        descriptor: classroom.descriptor,
        name: classroom.name,
        id: classroom.id,
        classCode: classroom.signupCode ?? "",
    };
};

/**
 * mapGqlAssignmentContent converts a GQL type for an assigned content to a
 * AssignmentContent type used in the Assignment type.
 */
export const mapGqlAssignmentContent = (
    assignment: GqlAssignment,
    content: GqlAssignmentContent,
): AssignmentContent | null | undefined => {
    if (content == null) {
        return null;
    }

    return {
        id: content.id,
        contentDescriptor: _invariant(
            content.contentDescriptor,
            "contentDescriptor",
        ),
        contentKind: _invariant<LearnableContentKind>(
            content.contentKind as LearnableContentKind | null | undefined,
            "contentKind",
        ),
        // @ts-expect-error - TS2339 - Property 'index' does not exist on type 'AssignmentData_contents'.
        quizIndex: content.contentKind === "Quiz" ? content.index : null,
        slug: _invariant<string>(content.slug, "slug"),
        translatedTitle: _invariant<string>(
            content.translatedTitle,
            "translatedTitle",
        ),
        urlWithinCurationNode: _invariant<string>(
            content.urlWithinCurationNode,
            "urlWithinCurationNode",
        ),
        topicPaths: (content.topicPaths ?? [])
            .filter(isTruthy)
            .map(mapGqlLearnableContentPath),
        unit: _mapAssignmentUnit(assignment, content),
    };
};

const _mapAssignmentUnit = (
    assignment: GqlAssignment,
    content: GqlAssignmentContent,
): AssignmentContentUnit => {
    const unit =
        assignment.lesson?.parent ??
        assignment.unit ??
        content.parentTopic?.parent;
    const {id, masteryEnabled, translatedTitle} = _invariant(
        unit,
        "assignment.content.parentTopic.parent",
    );
    return {
        id,
        masteryEnabled: !!masteryEnabled,
        translatedTitle: _invariant(
            translatedTitle,
            "assignment.content.parentTopic.parent.translatedTitle",
            {unitId: id},
        ),
    };
};

/**
 * mapGqlAssignment converts a GQL type for an Assignment to the expected
 * assignment type used in useClassroom.
 */
export const mapGqlAssignment = (assignment: GqlAssignment): Assignment => {
    const contents = _invariant(assignment.contents, "contents");
    if (contents.length > 1) {
        throw new KAError(
            "Unexpected number of content items found for assignment. Expected: 1, found: " +
                contents.length,
            Errors.Internal,
            {
                sentryData: {
                    contexts: {
                        Data: {
                            assignmentID: assignment.id,
                        },
                    },
                },
            },
        );
    }
    // Content can sometimes be null if it was deleted or if it is untranslated
    // in the current locale.
    const content = contents?.[0] ?? null;

    const completionStates = assignment.itemCompletionStates ?? [];
    if (completionStates.length > 1) {
        throw new KAError(
            "Unexpected number of completion states found for assignment. Expected: 1, found: " +
                completionStates.length,
            Errors.Internal,
            {
                sentryData: {
                    contexts: {
                        Data: {
                            assignmentID: assignment.id,
                        },
                    },
                },
            },
        );
    }
    const completionState = completionStates?.[0];
    const lastCompletedAttempt = (completionState?.exerciseAttempts ?? [])
        .filter((attempt) => attempt?.isCompleted && !attempt?.isRestarted)
        .pop();

    const {domainId, courseId, unitId, lessonId} = assignment;
    return {
        id: assignment.id,
        title: assignment.title,
        content: mapGqlAssignmentContent(assignment, content),
        dueDate: _invariant(assignment.dueDate, "dueDate"),
        itemCompletionState: mapGqlAssignmentCompletionState(
            completionState?.state,
        ),
        isSameQuestion:
            assignment.exerciseConfig?.itemPickerStrategy === "SAME",
        key: _invariant(assignment.key, "assignment.key"),
        lastAttempt: _mapGqlLastCompletedAttempt(lastCompletedAttempt),
        path: {
            domainId: _nonemptyOrNull(domainId),
            courseId: _nonemptyOrNull(courseId),
            unitId: _nonemptyOrNull(unitId),
            lessonId: _nonemptyOrNull(lessonId),
        },
    };
};

export const mapGqlAssignmentsPage = (
    assignmentsPage?: GqlAssignmentsPage | null,
    includeAIGuideActivities: boolean = false,
): AssignmentsPage => {
    const assignments = (assignmentsPage?.assignments ?? [])
        .filter(isTruthy)
        // Filter out Activity assignments is the user does not have
        // Khanmigo access
        // TODO CLASS-10005 - Move filtering to backend once Assignments
        // Unification supports filtering by content kind at data level
        .filter((assignment) => {
            if (
                !includeAIGuideActivities &&
                assignment.contents?.[0].contentKind === "AIGuideActivity"
            ) {
                return false;
            }

            return true;
        })
        .map(mapGqlAssignment);
    const nextCursor = assignmentsPage?.pageInfo?.nextCursor ?? null;
    return {
        assignments,
        pageInfo: {
            hasNextPage: nextCursor != null,
            nextCursor,
        },
    };
};

export const mapGqlAssignmentCompletionState = (
    state?: string | null,
): AssignmentCompletionState => {
    if (!state) {
        return "unstarted";
    }
    switch (state) {
        case "completed":
        case "missed":
        case "unstarted":
        case "in_progress":
        case "late":
            return state;
        default:
            throw new KAError(
                "Unexpected completion state: " + state,
                Errors.Internal,
            );
    }
};

const _mapGqlBestScore = (
    score?: GqlBestScore | null,
): BestScore | null | undefined => {
    if (!score) {
        return null;
    }
    return {
        numCorrect: score.numCorrect,
        numAttempted: score.numAttempted,
        completedDate: score.completedDate,
    };
};

const _mapGqlLastCompletedAttempt = (
    attempt:
        | GqlLastCompletedAttempt
        | null
        | undefined
        | GqlAssignmentExerciseAttempt
        | null
        | undefined,
): LastCompletedAttempt | null | undefined => {
    if (!attempt) {
        return null;
    }
    return {
        numCorrect: attempt.numCorrect,
        numAttempted: attempt.numAttempted,
        lastAttemptDate: attempt.lastAttemptDate,
    };
};

const _mapGqlLatestQuizOrUnitTestAttempt = (
    attempt?: GqlLatestQuizAttempt | GqlLatestUnitTestAttempt | null,
): LatestQuizOrUnitTestAttempt | null | undefined => {
    if (!attempt) {
        return null;
    }
    let key;
    if (attempt.__typename === "TopicQuizAttempt") {
        if (!attempt.positionKey) {
            throw new KAError("Missing positionKey", Errors.Internal);
        }
        key = attempt.positionKey;
    }
    if (attempt.__typename === "TopicUnitTestAttempt") {
        if (!attempt.topicId) {
            throw new KAError("Missing topicId", Errors.Internal);
        }
        key = `Unit:${attempt.topicId}`;
    }
    // We don't expect this to happen.  If it does, it means we can't link
    // the attempts to the quiz or unitTest in question.
    if (!key) {
        throw new KAError("Missing key", Errors.Internal);
    }
    return {
        key,
        numCorrect: attempt.numCorrect,
        numAttempted: attempt.numAttempted,
        isCompleted: attempt.isCompleted,
    };
};

const _mapGqlExerciseItemProgress = (
    progress: GqlExerciseItemProgress,
): ExerciseItemProgress => {
    return {
        type: "ExerciseItemProgress",
        completionStatus: _invariant(
            progress.completionStatus,
            "completionStatus",
        ),
        content: {
            id: _invariant(progress.content?.id, "id"),
            contentKind: _invariant(
                progress.content?.contentKind,
                "contentKind",
            ),
            contentDescriptor: _invariant(
                progress.content?.contentDescriptor,
                "contentDescriptor",
            ),
            progressKey: _invariant(
                progress.content?.progressKey,
                "progressKey",
            ),
        },
        masteryLevel: progress.updatedMasteryLevel || "unfamiliar",
        bestScore: _mapGqlBestScore(progress.bestScore),
        lastCompletedAttempt: _mapGqlLastCompletedAttempt(
            progress.lastCompletedAttempt,
        ),
    };
};

const _mapGqlContentItemProgress = (
    progress: GqlContentItemProgress,
): BasicContentItemProgress => {
    return {
        type: "BasicContentItemProgress",
        completionStatus: _invariant(
            progress.completionStatus,
            "completionStatus",
        ),
        content: {
            id: _invariant(progress.content?.id, "id"),
            contentKind: _invariant(
                progress.content?.contentKind,
                "contentKind",
            ),
            contentDescriptor: _invariant(
                progress.content?.contentDescriptor,
                "contentDescriptor",
            ),
            progressKey: _nonemptyOrNull(progress.content?.progressKey),
        },
        bestScore: _mapGqlBestScore(progress.bestScore),
    };
};

/**
 * mapGqlProgress converts a GQL type for a User's progress to the expected
 * progress map for useProgress.
 */
export const mapGqlProgress = (
    gqlProgresses: Array<GqlContentItemProgress | null | undefined> | null = [],
): Array<ContentItemProgress> => {
    return (gqlProgresses ?? [])
        .map((progress) => {
            if (!progress?.content) {
                return null;
            }
            if (progress.__typename === "ExerciseItemProgress") {
                return _mapGqlExerciseItemProgress(progress);
            }
            return _mapGqlContentItemProgress(progress);
        })
        .filter(isTruthy);
};

/**
 * mapGqlQuizAttempts converts an array of the GQL type for a User's
 * quiz attempts to an array of the business type for useLessonProgress.
 */
export const mapGqlQuizAttempts = (
    gqlAttempts: Array<GqlLatestQuizAttempt | null | undefined> | null = [],
): Array<LatestQuizOrUnitTestAttempt> => {
    return (gqlAttempts ?? [])
        .map(_mapGqlLatestQuizOrUnitTestAttempt)
        .filter(isTruthy);
};

/**
 * mapGqlUnitTestAttempts converts an array of the GQL type for a User's
 * unitTest attempts to an array of the business type for useLessonProgress.
 */
export const mapGqlUnitTestAttempts = (
    gqlAttempts: Array<GqlLatestUnitTestAttempt | null | undefined> | null = [],
): Array<LatestQuizOrUnitTestAttempt> => {
    return (gqlAttempts ?? [])
        .map(_mapGqlLatestQuizOrUnitTestAttempt)
        .filter(isTruthy);
};

/**
 * mapGqlCourseMasteryGoal converts a GqlCourseMasteryGoal type to a
 * CourseMasteryGoal type which is used to determine whether a user is
 * on task.
 */
export const mapGqlCourseMasteryGoal = (
    goal: GqlCourseMasteryGoal,
): CourseMasteryGoal => {
    // Topic can be null if you're viewing the site in a different locale from
    // where it was assigned
    const course = goal.topic ? {id: goal.topic.id} : null;

    return {
        id: goal.id,
        course,
    };
};

/**
 * _invariant marks a field as invariant for TypeScript and asserts that a field is
 * non-null otherwise, throws an error.
 */
const _invariant = <T>(
    field: T | null | undefined,
    fieldName: string,
    extraData?: any,
): T => {
    if (field == null) {
        throw new KAError(
            `Field is unexpectedly null when mapping GraphQL types: ${fieldName}`,
            Errors.Internal,
            {
                sentryData: {
                    contexts: {
                        Data: {
                            fieldName,
                            ...extraData,
                        },
                    },
                },
            },
        );
    }

    return field;
};

// Helper function that returns either null or a nonempty string
const _nonemptyOrNull = (s?: string | null): string | null | undefined => {
    // Using || instead of ?? because I want the type coercion in this case so
    // we can exclude empty strings
    return s || null;
};

/**
 * In the post-Goliath world, we no longer pass around entire UserProgram
 * models and instead only query for particular ProgramProgress fields. This
 * utility will take a Gql ProgramProgress types and map it to a subset of
 * UserProgram fields that our Backbone models can use.
 */
export const mapToUserProgram = (
    progress?: ProgramProgress | null,
): UserProgram | null | undefined => {
    if (!progress) {
        return null;
    }
    const contentKind = _invariant(
        progress.content?.contentKind,
        "contentKind",
    );
    const completionStatus = _invariant(
        progress.completionStatus,
        "completionStatus",
    );
    return {
        challengeStatus: mapCompletionStatusToChallengeStatus(
            completionStatus,
            contentKind,
        ),
        isComplete: completionStatus === "COMPLETE",
        points: progress.points,
        // @ts-expect-error - TS2339 - Property 'stashedCode' does not exist on type 'ProgramProgress'.
        stashedCode: progress.stashedCode,
        // @ts-expect-error - TS2339 - Property 'lastMsWatched' does not exist on type 'ProgramProgress'.
        lastMsWatched: progress.lastMsWatched,
    };
};

const _mapToUserAuthoredContentType = (
    userAuthoredContentType: null | undefined | string,
): UserAuthoredContentTypes => {
    return _invariant<UserAuthoredContentTypes>(
        userAuthoredContentType as UserAuthoredContentTypes | null | undefined,
        "userAuthoredContentType",
    );
};
